// 결과보고서 렌더러 판정의 단일 소스.
// 우선순위: 백엔드 스냅샷 필드(report_renderer_key) → 캠페인 ID env 화이트리스트 폴백.
// 백엔드 계약: df_backend_custom docs/multitenant-backend-contract.md §2
//   - /report/history 행: report_renderer_key, report_schema_version
//   - /report/data 및 저장 raw JSON: 최상위 report_meta.{report_renderer_key, report_schema_version}
//   - NULL/부재 = 레거시 미백필 → env 화이트리스트 폴백 (배포 순서 무관 안전)

// ⚠️ 순서 계약: generatePDF/뷰어 v-else-if 체인의 현행 분기 순서와 동일해야 한다.
// 캠페인 ID 가 여러 리스트에 중복 등록된 경우 첫 매치가 이기므로, 순서가 곧 동작이다.
export const RENDERER_ORDER = ['hmc', 'hmc_2025', 'hyundai_v2', 'hyundai', 'kia', 'ksteel', 'hmc_theme', 'supply'];

// 확정 캠페인 상수 — 백엔드 project_brands 등록·backfill 완료값 (2026-09-02, staging·prod 동일).
// hyundai_v2/hmc_theme env 는 이 배포의 Jenkins expectedKeys 에 없어서 상수가 실질 소스다.
// 보고서 판정은 여전히 스냅샷 필드 우선이고, 이 상수는 (a) 스냅샷 없는 화면(위젯 게이팅)과
// (b) 레거시 env 폴백을 보강한다. 신규 캠페인 등록 시 백엔드 DB 와 함께 갱신
// (ponytail: 캠페인 응답에 renderer_key 가 실리는 백엔드 계약이 생기면 이 상수를 걷어낸다).
const DEFAULT_CAMPAIGN_LISTS = {
    hyundai_v2: '550,551,552,553,554,555,556,557,558',
    hmc_theme: '560,561',
};

function withDefaults(raw, key) {
    return [raw, DEFAULT_CAMPAIGN_LISTS[key]].filter(Boolean).join(',');
}

// DefinePlugin 은 리터럴 멤버 접근(process.env.X)만 치환하므로 동적 인덱싱 금지 — 함수로 감싼다.
const ENV_WHITELIST_SOURCES = {
    hmc: () => process.env.VUE_APP_CAMPAIGN_LIST_HMC,
    hmc_2025: () => process.env.VUE_APP_CAMPAIGN_LIST_HMC_2025,
    hyundai_v2: () => withDefaults(process.env.VUE_APP_CAMPAIGN_LIST_HYUNDAI_V2, 'hyundai_v2'),
    hyundai: () => process.env.VUE_APP_CAMPAIGN_LIST_HYUNDAI,
    kia: () => process.env.VUE_APP_CAMPAIGN_LIST_KIA,
    ksteel: () => process.env.VUE_APP_CAMPAIGN_LIST_KSTEEL,
    hmc_theme: () => withDefaults(process.env.VUE_APP_CAMPAIGN_LIST_HMC_THEME, 'hmc_theme'),
    supply: () => process.env.VUE_APP_CAMPAIGN_LIST_SUPPLY,
};

// 렌더러 키의 캠페인 화이트리스트(파싱 완료 숫자 배열) — 위젯 게이팅/운영 도구용.
export function getRendererCampaignList(key) {
    const source = ENV_WHITELIST_SOURCES[key];
    return source ? parseCampaignList(source()) : [];
}

// 렌더러별 메타데이터. 순환 import 방지를 위해 컴포넌트 레퍼런스는 담지 않는다 —
// viewer 는 각 컨테이너의 components 등록명(문자열)이고, generate 는 client mixin 의 메서드명이다.
export const REPORT_RENDERERS = {
    hmc: { viewer: 'ReportViewerHMC', generate: 'generatePDF_hmc', assessmentTypeSource: 'selected', management: 'v1', appendCompanyToFilename: false },
    hmc_2025: { viewer: 'ReportViewerHMC_2025', generate: 'generatePDF_hmc_2025', assessmentTypeSource: 'selected', management: 'v1', appendCompanyToFilename: false },
    hyundai_v2: { viewer: 'ReportViewerHYUNDAI_V2', generate: 'generatePDF_hyundai_v2', assessmentTypeSource: 'selected', management: 'v1', appendCompanyToFilename: false },
    hyundai: { viewer: 'ReportViewerHYUNDAI', generate: 'generatePDF_hyundai', assessmentTypeSource: 'selected', management: 'v1', appendCompanyToFilename: false },
    kia: { viewer: 'ReportViewerKIA', generate: 'generatePDF_kia', assessmentTypeSource: 'selected', management: 'v1', appendCompanyToFilename: false },
    ksteel: { viewer: 'ReportViewerKSTEEL', generate: 'generatePDF_ksteel', assessmentTypeSource: 'prop', management: 'v2', appendCompanyToFilename: true },
    hmc_theme: { viewer: 'ReportViewerHMC_THEME', generate: 'generatePDF_hmc_theme', assessmentTypeSource: 'selected', management: 'v1', appendCompanyToFilename: true },
    supply: { viewer: 'ReportViewerSUPPLY', generate: 'generatePDF_supply', assessmentTypeSource: 'prop', management: 'v2', appendCompanyToFilename: true },
};

function parseCampaignList(raw) {
    if (raw == null) return [];
    return String(raw).split(',')
        .map(s => Number(s.trim()))
        .filter(n => Number.isSafeInteger(n) && n > 0);
}

// rendererKey(백엔드 스냅샷)가 유효하면 그 값, 아니면 campaignId 로 env 화이트리스트 첫 매치,
// 그것도 없으면 fallbackRenderer(보고서 소비자가 tenant.defaultRenderer 를 전달 — qesg=supply).
// fallbackRenderer 미전달(위젯 게이팅 등 strict 소비자) 시엔 종전대로 null.
// 배경: 신규 캠페인이 project_brands 미등록이면 스냅샷 NULL → 종전 fail-closed 는 보고서 빈 화면이
// 됐다 (2026-09-03 넥센 566~568 prod 장애). 미등록 캠페인은 그 도메인의 기본 보고서가 정답.
// 렌더러 키 별칭 — 'supply' 는 초기 supply.tierzero.kr 시절 잔재 명칭이고 정식 명칭은 'qesg'
// (탑다운 결정, 2026-09-03 유저 확정). 백엔드 brands.renderer_key 가 'qesg' 로 플립돼도
// 동일 렌더러로 해석된다. 레지스트리 내부 키의 풀 리네임은 별도 정리 항목.
const RENDERER_ALIASES = { qesg: 'supply' };

function canonicalRendererKey(key) {
    if (typeof key !== 'string') return key;
    return Object.prototype.hasOwnProperty.call(RENDERER_ALIASES, key) ? RENDERER_ALIASES[key] : key;
}

export function resolveReportTemplate({ rendererKey, campaignId, fallbackRenderer } = {}) {
    rendererKey = canonicalRendererKey(rendererKey);
    fallbackRenderer = canonicalRendererKey(fallbackRenderer);
    if (typeof rendererKey === 'string' && RENDERER_ORDER.includes(rendererKey)) return rendererKey;
    const fallback = typeof fallbackRenderer === 'string' && RENDERER_ORDER.includes(fallbackRenderer) ? fallbackRenderer : null;
    const id = Number(campaignId);
    if (!Number.isSafeInteger(id) || id <= 0) return fallback;
    for (const key of RENDERER_ORDER) {
        if (parseCampaignList(ENV_WHITELIST_SOURCES[key]()).includes(id)) return key;
    }
    return fallback;
}

// 캠페인 ID 가 여러 화이트리스트에 중복 등록되면 첫 매치 순서에 묻혀 조용히 넘어간다.
// 설정 오류를 운영에서도 볼 수 있도록 항상 경고한다 (모듈 로드 시 1회, 비용 무시 가능).
{
    const seen = new Map();
    for (const key of RENDERER_ORDER) {
        for (const id of parseCampaignList(ENV_WHITELIST_SOURCES[key]())) {
            if (seen.has(id)) console.warn(`[report-template] 캠페인 ID ${id} 가 ${seen.get(id)} 와 ${key} 리스트에 중복 등록됨 — ${seen.get(id)} 가 우선`);
            else seen.set(id, key);
        }
    }
}
